import { Component } from '@theme/component';
import { OverlayOpenRequestEvent } from '@theme/dialog';
import { removeTrapFocus, trapFocus } from '@theme/focus';
import { acquireScrollLock, releaseScrollLock } from '@theme/scroll-lock';
import { debounce } from '@theme/utilities';

const ACTIVATE_DELAY = 0;
const SWITCH_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 500;

// Maps the header section's `desktop_nav_bp` setting (mirrored on
// `<header-component data-nav-bp="...">`) to the matching min-width
// query. Keep these px values in sync with `--bp-md/lg/xl` in
// `breakpoints.css` and the matching media queries in `nav-menu.css`
// and `header-custom.liquid`.
const DESKTOP_QUERY_BY_BP = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)'
};
const DEFAULT_DESKTOP_BP = 'lg';
const NAV_MENU_ANCHOR_STORAGE_KEY = 'ly-nav-mobile-last-menu';

/**
 * Dual-mode nav menu. The desktop threshold is configurable per header
 * section via `data-nav-bp` (md/lg/xl) on the closest `<header-component>`
 * ancestor; defaults to `lg` (1024px).
 *   - Desktop: hover-driven flyouts in the header bar.
 *   - Mobile: the element itself is the slide-in drawer; tapping a
 *     trigger swaps in its Level 2 panel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu]
 * @property {HTMLButtonElement} [back]
 * @property {HTMLElement} [breadcrumb]
 *
 * @extends {Component<Refs>}
 */
class NavMenu extends Component {
  _state = {
    /** @type {Element | null} */
    activeItem: null,
    pinned: false,
    mobileOpen: false
  };

  /** @type {number | undefined} */
  #switchTimeout;

  /** @type {MediaQueryList | null} */
  #desktopMql = null;

  /**
   * Element that held focus before the mobile drawer opened (usually the
   * hamburger button). Restored on close so keyboard / SR users land back
   * on the trigger instead of `<body>`.
   *
   * @type {HTMLElement | null}
   */
  #returnFocusTarget = null;

  /** @type {HTMLElement | null} */
  #scrim = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onNavNavigationClick);

    const bp = this.closest('[data-nav-bp]')?.getAttribute('data-nav-bp') ?? DEFAULT_DESKTOP_BP;
    const query = DESKTOP_QUERY_BY_BP[bp] ?? DESKTOP_QUERY_BY_BP[DEFAULT_DESKTOP_BP];
    this.#desktopMql = matchMedia(query);
    this.#desktopMql.addEventListener('change', this.#onLayoutChange);
  }

  disconnectedCallback() {
    this.#cancelSwitchTimeout();
    document.removeEventListener('click', this.#onDocumentClickWhilePinned, true);
    document.removeEventListener('click', this.#onDocumentClickWhileMobileOpen, true);
    this.#getScrim()?.removeEventListener('click', this.#onScrimClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onNavNavigationClick);
    this.#desktopMql?.removeEventListener('change', this.#onLayoutChange);
    this.#desktopMql = null;
    if (this._state.mobileOpen) removeTrapFocus();
    this.#returnFocusTarget = null;
    releaseScrollLock(this);
    super.disconnectedCallback();
  }

  /** @returns {boolean} */
  get #isDesktop() {
    return this.#desktopMql?.matches ?? false;
  }

  /**
   * Hover-activate (desktop only — mobile uses `toggleDesktopSubmenu`).
   *
   * @param {Event} event
   */
  activate = (event) => {
    if (!this.#isDesktop) return;

    this._debouncedDeactivate.cancel();
    this._debouncedActivate.cancel();
    this.#cancelSwitchTimeout();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.nav-menu__item');
    const menuitem = item?.querySelector('[ref="menuitem"]');

    if (!menuitem) {
      if (this._state.activeItem) {
        this._state.pinned = false;
        this._deactivateHandler();
        document.removeEventListener('click', this.#onDocumentClickWhilePinned, true);
      }
      return;
    }

    if (event.type === 'pointerenter' && this._state.activeItem && menuitem !== this._state.activeItem) {
      this.#switchTimeout = window.setTimeout(() => {
        this._activateHandler(event);
      }, SWITCH_ACTIVATE_DELAY);
      return;
    }

    this._debouncedActivate(event);
  };

  _activateHandler = (event) => {
    this._debouncedDeactivate.cancel();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.nav-menu__item');
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (!menuitem || menuitem === this._state.activeItem) return;

    this._state.pinned = false;

    const previous = this._state.activeItem;
    // Tag a switch (an existing flyout swapping to a different one) so CSS
    // can suppress transitions for this single frame. Both submenus share
    // the same fixed position + white panel BG, so killing the fade keeps
    // the visible surface continuous while only the content swaps.
    const isSwitch = previous !== null && previous !== menuitem;
    if (isSwitch) this.setAttribute('data-switching', '');

    if (previous) previous.setAttribute('aria-expanded', 'false');

    this._state.activeItem = menuitem;
    menuitem.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-active', '');
    acquireScrollLock(this);

    if (isSwitch) {
      // Force style recalc so the browser captures the property changes with
      // `transition: none` from `[data-switching]`. `requestAnimationFrame`
      // alone fires before style recalc — too early — and the transitions
      // would still kick in.
      void this.offsetHeight;
      this.removeAttribute('data-switching');
    }
  };

  _debouncedActivate = debounce(this._activateHandler, ACTIVATE_DELAY);

  /**
   * Hover-leave (desktop only). Closes after `DEACTIVATE_DELAY` if the
   * pointer doesn't return.
   *
   * @param {Event} event
   */
  deactivate = (event) => {
    if (!this.#isDesktop) return;
    if (this._state.pinned) return;

    this._debouncedActivate.cancel();
    this.#cancelSwitchTimeout();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.nav-menu__item');
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (menuitem === this._state.activeItem) {
      this._debouncedDeactivate();
    }
  };

  _deactivateHandler = (item = this._state.activeItem) => {
    if (!item || item !== this._state.activeItem) return;

    this._state.pinned = false;
    this._state.activeItem = null;
    item.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-active');
    // Mobile drawer still owns the scroll-lock until the hamburger closes it.
    if (!this._state.mobileOpen) {
      releaseScrollLock(this);
    }
  };

  _debouncedDeactivate = debounce(this._deactivateHandler, DEACTIVATE_DELAY);

  /**
   * Click handler on trigger anchors. Behavior by layout:
   *   - Desktop + hover pointer: click follows the link.
   *   - Desktop + touch / coarse pointer: first tap pins the flyout
   *     open, second tap (or outside-click) closes it.
   *   - Mobile: always intercepts and opens Level 2 over Level 1.
   *
   * @param {Event} event
   */
  toggleDesktopSubmenu = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.nav-menu__item');
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (!menuitem) return;

    if (!this.#isDesktop) {
      event.preventDefault();
      this.#openMobileSubmenu(menuitem);
      return;
    }

    // iPadOS reports (hover: hover) like a desktop, so check all three
    // pointer/hover signals to catch touch-capable laptops too.
    const prefersTapMenuOverLinkNavigation =
      matchMedia('(hover: none)').matches ||
      matchMedia('(pointer: coarse)').matches ||
      matchMedia('(any-pointer: coarse)').matches;

    if (!prefersTapMenuOverLinkNavigation) return;

    event.preventDefault();

    if (menuitem === this._state.activeItem && this._state.pinned) {
      this._state.pinned = false;
      this._debouncedDeactivate.cancel();
      this._deactivateHandler(menuitem);
      document.removeEventListener('click', this.#onDocumentClickWhilePinned, true);
      return;
    }

    this._debouncedDeactivate.cancel();
    this._debouncedActivate.cancel();
    this._state.pinned = true;

    if (menuitem !== this._state.activeItem) {
      this._activateHandler(event);
    }

    requestAnimationFrame(() => {
      document.addEventListener('click', this.#onDocumentClickWhilePinned, true);
    });
  };

  /** Toggles the mobile drawer (wired to the hamburger). */
  toggleMobileMenu = () => {
    if (this._state.mobileOpen) {
      this.#closeMobile();
    } else {
      this.#openMobile();
    }
  };

  /** Closes the active Level 2 panel and returns to Level 1 (mobile only). */
  closeMobileSubmenu = () => {
    if (!this._state.activeItem) return;
    this._deactivateHandler(this._state.activeItem);
    this.#updateBreadcrumb();
  };

  /** @param {Element} menuitem */
  #openMobileSubmenu(menuitem) {
    if (this._state.activeItem && this._state.activeItem !== menuitem) {
      this._state.activeItem.setAttribute('aria-expanded', 'false');
    }

    this._state.activeItem = menuitem;
    menuitem.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-active', '');
    this.#updateBreadcrumb();

    const item = menuitem.closest('.nav-menu__item');
    if (item) this.#rememberOverlayMenu(item);
  }

  #openMobile() {
    if (this._state.mobileOpen) return;
    document.dispatchEvent(new OverlayOpenRequestEvent(this));
    this.#returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this._state.mobileOpen = true;
    this.setAttribute('data-mobile-open', '');
    acquireScrollLock(this);
    this.#setHamburgerExpanded(true);
    document.addEventListener('click', this.#onDocumentClickWhileMobileOpen, true);
    this.#getScrim()?.addEventListener('click', this.#onScrimClick);

    this.#resolveAnchoredMenuitem();
    trapFocus(this);
  }

  #closeMobile() {
    if (!this._state.mobileOpen) return;
    this._state.mobileOpen = false;
    this.#revealHeaderAfterMobileClose({ pendingScrollRestore: true });
    this.removeAttribute('data-mobile-open');
    // Drop any open Level 2 so reopening re-anchors on the next open.
    if (this._state.activeItem) {
      this._state.activeItem.setAttribute('aria-expanded', 'false');
      this._state.activeItem = null;
      this.removeAttribute('data-active');
      this.#updateBreadcrumb();
    }
    releaseScrollLock(this);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#revealHeaderAfterMobileClose();
      });
    });
    this.#setHamburgerExpanded(false);
    document.removeEventListener('click', this.#onDocumentClickWhileMobileOpen, true);
    this.#getScrim()?.removeEventListener('click', this.#onScrimClick);
    removeTrapFocus();
    this.#returnFocusTarget?.focus();
    this.#returnFocusTarget = null;
  }

  /**
   * @param {{ pendingScrollRestore?: boolean }} [options]
   */
  #revealHeaderAfterMobileClose(options = {}) {
    const header = document.querySelector('header-component');

    header?.dispatchEvent(new CustomEvent('revealUntilScrollDown', { detail: options }));
  }

  /**
   * Closes the drawer on outside-tap. Ignores the hamburger — its own
   * `toggleMobileMenu` handler runs first and would otherwise re-open.
   *
   * @param {MouseEvent} event
   */
  #onDocumentClickWhileMobileOpen = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.nav-menu__scrim')) {
      this.#closeMobile();
      return;
    }
    if (event.target.closest('nav-menu') === this) return;
    if (event.target.closest('[aria-controls="header-nav-menu"]')) return;
    this.#closeMobile();
  };

  #onScrimClick = () => {
    this.#closeMobile();
  };

  #getScrim() {
    if (!this.#scrim?.isConnected) {
      this.#scrim = this.parentElement?.querySelector('.nav-menu__scrim') ?? null;
    }

    return this.#scrim;
  }

  /** @param {boolean} expanded */
  #setHamburgerExpanded(expanded) {
    const hamburger = document.querySelector('[aria-controls="header-nav-menu"]');
    hamburger?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Remember which overlay menu the shopper last opened or navigated from,
   * so the next mobile drawer open can restore that Level 2. Direct landings
   * (no menu interaction) stay on Level 1.
   *
   * @param {MouseEvent} event
   */
  #onNavNavigationClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const item = link.closest('.nav-menu__item[data-menu-type="overlay"]');
    if (!item) return;

    this.#rememberOverlayMenu(item);
  };

  /**
   * @param {Element} item
   */
  #rememberOverlayMenu(item) {
    const key = this.#getOverlayMenuKey(item);
    if (!key) return;

    try {
      sessionStorage.setItem(NAV_MENU_ANCHOR_STORAGE_KEY, key);
    } catch {
      // Private mode / blocked storage — skip persistence.
    }
  }

  /**
   * @returns {string | null}
   */
  #getRememberedOverlayMenuKey() {
    try {
      return sessionStorage.getItem(NAV_MENU_ANCHOR_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * @param {Element} item
   * @returns {string | null}
   */
  #getOverlayMenuKey(item) {
    const handle = item.getAttribute('data-nav-handle')?.toLowerCase();
    if (handle) return `handle:${handle}`;

    const menu = item.getAttribute('data-nav-menu');
    if (menu) return `menu:${menu}`;

    return null;
  }

  /**
   * @param {string} key
   * @returns {Element | null}
   */
  #findMenuitemByOverlayMenuKey(key) {
    for (const item of this.querySelectorAll('.nav-menu__item[data-menu-type="overlay"]')) {
      if (this.#getOverlayMenuKey(item) !== key) continue;

      return item.querySelector('[ref="menuitem"]');
    }

    return null;
  }

  /**
   * Restore the last menu the shopper opened via nav. No remembered menu
   * means Level 1 only — e.g. landed directly on a collection URL.
   */
  #resolveAnchoredMenuitem() {
    if (this.#isDesktop) return;

    const rememberedKey = this.#getRememberedOverlayMenuKey();
    if (!rememberedKey) return;

    const menuitem = this.#findMenuitemByOverlayMenuKey(rememberedKey);
    if (menuitem) this.#openMobileSubmenu(menuitem);
  }

  /**
   * Reflects the active top-level menu name into the back-bar label
   * (e.g. "Women" while the Women flyout is open).
   */
  #updateBreadcrumb() {
    const target = this.refs.breadcrumb;
    if (!target) return;

    if (!this._state.activeItem) {
      target.textContent = '';
      return;
    }

    target.textContent = this._state.activeItem.textContent.trim();
  }

  #onDocumentClickWhilePinned = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('nav-menu') === this) return;

    this._state.pinned = false;
    this._deactivateHandler();
    document.removeEventListener('click', this.#onDocumentClickWhilePinned, true);
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    // Roll back one level: Level 2 → Level 1 → drawer closed.
    if (this._state.activeItem) {
      const active = this._state.activeItem;
      this._state.pinned = false;
      this._deactivateHandler(active);
      document.removeEventListener('click', this.#onDocumentClickWhilePinned, true);
      this.#updateBreadcrumb();
      if (active instanceof HTMLElement) active.focus();
      return;
    }

    if (this._state.mobileOpen) {
      this.#closeMobile();
    }
  };

  #cancelSwitchTimeout() {
    window.clearTimeout(this.#switchTimeout);
    this.#switchTimeout = undefined;
  }

  /**
   * Reset state when crossing the desktop breakpoint — bleeding mobile
   * drawer state into the desktop flyout (or vice-versa) looks broken.
   */
  #onLayoutChange = () => {
    if (this._state.activeItem) {
      this._deactivateHandler(this._state.activeItem);
    }
    if (this._state.mobileOpen) {
      this.#closeMobile();
    }
  };
}

if (!customElements.get('nav-menu')) {
  customElements.define('nav-menu', NavMenu);
}
